import React from "react";
import { Mail } from "lucide-react";

import Button from "@/components/ui/Button.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import {
  isValidAccountEmail,
  normalizeAccountEmail,
  requestAccountEmailChange,
} from "@/pages/profile-settings/accountEmailChangeService.js";

function readableError(error) {
  const message = String(error?.message || error || "");
  if (/duplicate|already/i.test(message)) return "That email is already used by another AfriMed™ account or active membership.";
  if (/same_email/i.test(message)) return "Enter a different email address.";
  return message || "We couldn’t start a secure email change. Please try again.";
}

/** A compact Account action: the visible identity stays read-only until verified. */
export default function AccountEmailChangeAction({ lane, currentEmail, disabled = false, onPendingChange }) {
  const [open, setOpen] = React.useState(false);
  const [newEmail, setNewEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [message, setMessage] = React.useState("");

  const close = () => {
    if (sending) return;
    setOpen(false);
    setMessage("");
    setNewEmail("");
  };

  async function submit(event) {
    event?.preventDefault?.();
    const cleanEmail = normalizeAccountEmail(newEmail);
    if (!isValidAccountEmail(cleanEmail)) {
      setMessage("Enter a valid new email address.");
      return;
    }
    if (cleanEmail === normalizeAccountEmail(currentEmail)) {
      setMessage("Enter a different email address.");
      return;
    }

    setSending(true);
    setMessage("");
    try {
      const pending = await requestAccountEmailChange({ lane, newEmail: cleanEmail });
      onPendingChange?.(pending);
      setMessage("A secure confirmation link has been sent. Your current email remains active until verification succeeds.");
    } catch (error) {
      setMessage(readableError(error));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="px-1 py-0.5">
      <Button
        type="button"
        variant="text"
        className="min-h-10 gap-2 rounded-lg px-3"
        onClick={() => setOpen(true)}
        disabled={disabled || !currentEmail}
      >
        <Mail className="h-4 w-4" aria-hidden />
        Change email
      </Button>

      {open ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="presentation">
          <button className="absolute inset-0 bg-black/35" type="button" aria-label="Close change email" onClick={close} />
          <form
            className="relative z-10 w-full max-w-[480px] rounded-[24px] bg-[rgb(var(--clr-surface-cont-highest))] p-6 shadow-xl"
            onSubmit={submit}
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-email-change-title"
          >
            <h2 id="account-email-change-title" className="text-title-large text-on-surface">Change email</h2>
            <p className="mt-2 text-body-medium text-on-surface-var">
              We’ll send a secure confirmation link to the new address. Your current sign-in email remains active until that verification succeeds.
            </p>
            <div className="mt-5">
              <TextField56
                label="New work email"
                type="email"
                autoComplete="email"
                value={newEmail}
                onChange={(value) => setNewEmail(typeof value === "string" ? value : value?.target?.value || "")}
                placeholder="you@example.com"
                tone="surface"
                disabled={sending}
              />
            </div>
            {message ? <p className="mt-3 text-body-small text-on-surface-var" role="status">{message}</p> : null}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="tonal" onClick={close} disabled={sending}>Cancel</Button>
              <Button type="submit" disabled={sending}>{sending ? "Sending…" : "Send confirmation"}</Button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
}
